import useRequest from "@/lib/request/useRequest";
import { Organization } from "@/types/organization";
import { PaginationResponse } from "@/types/pagination";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
    faCheck,
    faLeaf,
    faFileZipper,
    faClipboard,
    faDrawPolygon,
    faArrowUpRightFromSquare,
    faCheckDouble,
    faBars,
    faPlus,
    faChevronDown,
    faTractor,
    faLayerGroup,
    faBucket,
    faFilter,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Divider,
    ModalBody,
    ModalHeader,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    addToast,
    Modal,
    ModalContent,
    ModalFooter,
    Input,
} from "@heroui/react";
import {
    ColumnDef,
    SortingState,
    VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { User } from "@/types/user";
import { useInfiniteQuery } from "@tanstack/react-query";
import BulkPlantingOperationForm from "@/components/home/BulkPlantingOperationForm";
import BulkRxExport from "@/components/home/BulkRxExport";
import BulkLogApplication from "@/components/home/BulkLogApplication";
import { FieldConfig } from "@/types/fieldConfig";
import BulkPlotRxForm from "@/components/home/BulkPlotRxForm";
import InfoModal from "@/components/common/InfoModal";
import BulkConfirmPlots from "@/components/home/BulkConfirmPlots";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import {
    createFieldColumns,
    FieldColumnOptions,
} from "@/components/fields/fieldColumns";
import TableV2 from "@/components/table/TableV2";
import FieldPlanner from "@/components/home/planner/FieldPlanner";
import BulkNPlanner from "@/components/home/planner/BulkNPlanner";
import ScenarioPlannerIcon from "@/components/nav/ScenarioPlannerIcon";
import useAuth from "@/lib/auth/useAuth";
import SoilManagement from "@/components/home/SoilManagement";
import ProductAutoComplete from "@/components/inputs/ProductAutoComplete";
import useBulkBoundaryDownload from "@/lib/utils/useBulkBoundaryDownload";
import useIsMobile, { DESKTOP_BREAKPOINT } from "@/lib/utils/useIsMobile";
import MobileFilterDrawer from "@/components/home/MobileFilterDrawer";
import MobileFieldConfigCard from "@/components/home/MobileFieldConfigCard";
import MobileFieldConfigDetailDrawer from "@/components/home/MobileFieldConfigDetailDrawer";
import {
    ManagementZoneChip,
    PlotStatusCheckpoints,
    PlotStatusChip,
    NPlanButton,
    SoilManagementButton,
    ActiveCropCell,
    ConnectionsCell,
    isSoilIncomplete,
} from "@/components/home/FieldConfigCells";

type FieldConfigSearch = {
    search?: string;
    ordering?: string;
    service_provider?: string;
    grower?: string;
    account_manager?: string;
    enrollment_tier?: string;
    created_date?: string;
    columns?: string;
};

export const Route = createFileRoute("/dashboard/home/field-config")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): FieldConfigSearch => {
        return {
            search: search.search as string,
            ordering: search.ordering as string,
            service_provider: search.service_provider as string,
            grower: search.grower as string,
            account_manager: search.account_manager as string,
            enrollment_tier: search.enrollment_tier as string,
            created_date: search.created_date as string,
            columns: search.columns as string,
        };
    },
});

const COLUMN_VISIBILITY_STORAGE_KEY = "field-config-column-visibility";

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
    selection: true,
    grower: true,
    name: true,
    activeCrop: true,
    irrigation: true,
    managementZone: true,
    connections: true,
    nPlan: true,
    soil: true,
};

const VALID_COLUMN_IDS = new Set(Object.keys(DEFAULT_COLUMN_VISIBILITY));

const FIELD_CONFIG_SORT_OPTIONS = [
    { key: "name", label: "Name" },
    { key: "grower", label: "Farm" },
];

function parseColumnsFromUrl(
    columnsParam: string | undefined,
): VisibilityState {
    if (!columnsParam?.trim()) {
        return { ...DEFAULT_COLUMN_VISIBILITY };
    }
    const visibleIds = columnsParam
        .split(",")
        .map((id) => id.trim())
        .filter((id) => VALID_COLUMN_IDS.has(id));
    const visibility = { ...DEFAULT_COLUMN_VISIBILITY };
    for (const id of Object.keys(visibility)) {
        (visibility as Record<string, boolean>)[id] = visibleIds.includes(id);
    }
    return visibility;
}

function visibilityToColumnParam(visibility: VisibilityState): string {
    return Object.entries(visibility)
        .filter(([, visible]) => visible)
        .map(([id]) => id)
        .join(",");
}

function parseColumnsFromStorage(
    stored: string | null,
): VisibilityState | null {
    try {
        if (!stored) return null;
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        return { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
    } catch {
        return null;
    }
}

function RouteComponent() {
    const { useGetRequest, axiosRequest, useMutateRequest } = useRequest();

    const {
        search,
        ordering,
        service_provider,
        grower,
        account_manager,
        enrollment_tier,
        created_date,
        columns: columnsParam,
    } = Route.useSearch();

    const navigate = Route.useNavigate();
    const { userIsSentinel, isConsultantOrSentinel } = useAuth();
    const { isMobile } = useIsMobile();
    const { isMobile: compactFilters } = useIsMobile(DESKTOP_BREAKPOINT);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [detailField, setDetailField] = useState<FieldConfig | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState(search ?? "");

    useEffect(() => {
        setDebouncedSearch(search ?? "");
    }, [search]);

    useEffect(() => {
        if (!compactFilters) return;
        if (debouncedSearch === (search ?? "")) return;
        const handler = setTimeout(() => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    search: debouncedSearch === "" ? undefined : debouncedSearch,
                }),
            });
        }, 700);

        return () => clearTimeout(handler);
    }, [compactFilters, debouncedSearch, navigate, search]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: isConsultantOrSentinel,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const growersById = useMemo(() => {
        return (
            growersQuery.data?.reduce(
                (acc, grower) => {
                    acc[grower.id] = grower;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [growersQuery.data]);

    const serviceProvidersById = useMemo(() => {
        return (
            serviceProvidersQuery.data?.reduce(
                (acc, serviceProvider) => {
                    acc[serviceProvider.id] = serviceProvider;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [serviceProvidersQuery.data]);

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const fieldsQuery = useInfiniteQuery<PaginationResponse<FieldConfig>>({
        queryKey: [
            "fields",
            "config",
            search,
            ordering || "name",
            service_provider,
            grower,
            account_manager,
            enrollment_tier,
            created_date,
        ],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<FieldConfig>>({
                url: "fields/config/",
                params: {
                    search,
                    ordering: ordering || "name",
                    service_provider,
                    grower,
                    account_manager,
                    enrollment_tier,
                    created_date,
                    page: pageParam,
                },
            });
        },
    });

    const tableData = useMemo(() => {
        return fieldsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    }, [fieldsQuery.data?.pages]);

    const columns: ColumnDef<FieldConfig>[] = useMemo(() => {
        const columnOptions: FieldColumnOptions = {
            growersById,
            serviceProvidersById,
            accountManagersById,
            userIsSentinel,
        };

        const cols: ColumnDef<FieldConfig>[] = [
            createFieldColumns.selection<FieldConfig>(),
            createFieldColumns.growerAndServiceProvider<FieldConfig>(
                columnOptions,
            ),
            createFieldColumns.fieldName<FieldConfig>(),
            {
                id: "activeCrop",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2 ">
                            Active Crop
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Active Crop
                                </ModalHeader>
                                <ModalBody>
                                    <ul className="list-disc ml-6 space-y-2">
                                        <li>
                                            The active crop & planting date are
                                            used to maximize the accuracy of
                                            Sentinel insights
                                        </li>
                                        <li>
                                            The planting date does not need to
                                            be exact if the info isn't
                                            available. It can always be updated
                                            later.
                                        </li>
                                        <li>
                                            The default crop and planting date
                                            is Corn planted on May 1st.
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                enableSorting: false,
                accessorFn: (row) =>
                    row?.active_planting_operation?.crop_configuration
                        ?.crop_name ?? "",
                cell: ({ row }) => {
                    const planting = row.original.active_planting_operation;
                    return (
                        <ActiveCropCell
                            cropName={
                                planting?.crop_configuration?.crop_name
                            }
                            plantingDate={planting?.start_date}
                            plantingOperationId={planting?.id}
                        />
                    );
                },
                size: 180,
            },
            {
                id: "plotStatus",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2 ">
                            Plot Status
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Plot Status
                                </ModalHeader>
                                <ModalBody>
                                    <h3 className="font-semibold mb-2">
                                        Sentinel Plots need to be implemented to
                                        create high and low Nitrogen
                                        environments and power Insights.
                                    </h3>
                                    <ul className="list-disc ml-6 space-y-2">
                                        <li>Create a Sentinel Plot Rx</li>
                                        <li>
                                            Download the Rx as a file or export
                                            it directly to your connections
                                        </li>
                                        <li>
                                            Log the application to activate
                                            plots for Insights
                                        </li>
                                        <li>
                                            Confirm the plots locations for
                                            accuracy
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) => row?.plot_status ?? "",
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col items-start justify-center w-full gap-1">
                            <PlotStatusCheckpoints
                                field={row.original}
                                className="ml-3"
                            />
                            <PlotStatusChip field={row.original} />
                        </div>
                    );
                },
                size: 180,
            },
            createFieldColumns.irrigation<FieldConfig>(),
            {
                id: "managementZone",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2">
                            Zones
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Zones
                                </ModalHeader>
                                <ModalBody>
                                    <ul>
                                        <li>
                                            This indicates whether the field has
                                            a primary management zone.
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) =>
                    row?.has_primary_management_zones ? "Yes" : "No",
                enableSorting: false,
                cell: ({ row }) => {
                    return <ManagementZoneChip field={row.original} />;
                },
                size: 100,
            },
            {
                id: "connections",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2">
                            Connections
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Connections
                                </ModalHeader>
                                <ModalBody className="flex flex-col gap-2">
                                    <ul>
                                        <li>
                                            This indicates whether the field is
                                            connected to an external system like
                                            John Deere Operations Center, Case
                                            IH AFS Connect, etc.
                                        </li>
                                        <li></li>
                                    </ul>
                                    <Button
                                        as={Link}
                                        to="/dashboard/account-settings/connections"
                                        target="_blank"
                                        color="primary"
                                        fullWidth
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowUpRightFromSquare}
                                            />
                                        }
                                    >
                                        Go to Connections Page
                                    </Button>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) => row?.connections_list,
                enableSorting: false,
                cell: ({ row }) => {
                    return <ConnectionsCell field={row.original} />;
                },
                size: 130,
            },
            {
                id: "nPlan",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2 justify-center w-full">
                            N Plan
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    N Plan
                                </ModalHeader>
                                <ModalBody className="flex flex-col gap-2">
                                    <ul>
                                        <li>
                                            Create and compare different
                                            nitrogen management plans for the
                                            upcoming growing season
                                        </li>
                                        <li></li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) => row.primary_application_plan_name,
                cell: ({ row }) => {
                    return (
                        <NPlanButton
                            field={row.original}
                            onPress={() => setPlannerField(row.original)}
                        />
                    );
                },
                size: 120,
            },
            {
                id: "soil",
                header: "Soil & Management",
                accessorFn: (row) => row?.id,
                cell: ({ row }) => {
                    return (
                        <SoilManagementButton
                            incomplete={
                                row.original.soil_management_required &&
                                isSoilIncomplete(
                                    row.original,
                                    row.original.current_crop_cycle,
                                )
                            }
                            onPress={() =>
                                setSoilManagementFields([row.original])
                            }
                        />
                    );
                },
                size: 180,
            },
        ];

        if (!userIsSentinel) {
            return cols.filter((col) => col.id !== "nPlan");
        }

        return cols;
    }, [
        growersById,
        serviceProvidersById,
        accountManagersById,
        userIsSentinel,
    ]);

    const [sorting, setSorting] = useState<SortingState>([
        ordering
            ? {
                id: ordering,
                desc: ordering.startsWith("-"),
            }
            : {
                id: "name",
                desc: false,
            },
    ]);

    const [selectedFields, setSelectedFields] = useState<FieldConfig[]>([]);
    const selectedFieldIds = useMemo(() => {
        return selectedFields.map((field) => field.id);
    }, [selectedFields]);
    const { downloadBoundaries, isDownloading } =
        useBulkBoundaryDownload(selectedFieldIds);

    useEffect(() => {
        if (sorting.length > 0) {
            const id = sorting[0].id.startsWith("-")
                ? sorting[0].id.slice(1)
                : sorting[0].id;
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    const isLoading = useMemo(() => {
        return (
            fieldsQuery.isLoading ||
            serviceProvidersQuery.isLoading ||
            growersQuery.isLoading
        );
    }, [
        fieldsQuery.isLoading,
        growersQuery.isLoading,
        serviceProvidersQuery.isLoading,
    ]);

    const mobileIsLoading = fieldsQuery.isLoading && tableData.length === 0;
    const activeMobileFilterCount = useMemo(() => {
        return [
            search?.trim(),
            service_provider,
            grower,
            account_manager,
            created_date,
        ].filter(Boolean).length;
    }, [account_manager, created_date, grower, search, service_provider]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const accountManagerOptions = useMemo(() => {
        return (
            accountManagersQuery.data?.map((accountManager) => ({
                label: accountManager.full_name,
                key: accountManager.id.toString(),
            })) ?? []
        );
    }, [accountManagersQuery.data]);

    const handleSelectionChange = (selected: FieldConfig[]) => {
        setSelectedFields(selected);
    };

    const [showPlantingForm, setShowPlantingForm] = useState<boolean>(false);
    const [showPlotRxForms, setShowPlotRxForms] = useState<boolean>(false);
    const [showBulkExport, setShowBulkExport] = useState<boolean>(false);
    const [showLogApplicationForm, setShowLogApplicationForm] =
        useState<boolean>(false);
    const [showPlotConfirmation, setShowPlotConfirmation] =
        useState<boolean>(false);
    const [showNPlanner, setShowNPlanner] = useState<boolean>(false);
    const [plannerField, setPlannerField] = useState<FieldConfig | null>(null);
    const [soilManagementFields, setSoilManagementFields] = useState<
        FieldConfig[]
    >([]);
    const [showDefaultProductForm, setShowDefaultProductForm] =
        useState<boolean>(false);
    const [selectedDefaultProductId, setSelectedDefaultProductId] = useState<
        string | null
    >(null);

    const patchField = useMutateRequest({
        method: "patch",
        url: "/fields/bulk_update/",
        successMessage: "Fields updated successfully.",
        errorMessage: "Failed to update fields.",
    });

    const meta = useMemo(() => {
        return {
            updateDrainage: (fieldId: number, value: boolean) => {
                axiosRequest({
                    url: `fields/${fieldId}/`,
                    method: "patch",
                    data: {
                        has_drainage: value,
                    },
                }).then(() => {
                    addToast({
                        title: "Drainage updated successfully",
                        color: "success",
                    });
                });
            },
        };
    }, [axiosRequest]);

    const loadedFromStorageRef = useRef(false);

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        () => {
            if (columnsParam?.trim()) {
                return parseColumnsFromUrl(columnsParam);
            }
            const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            const fromStorage = parseColumnsFromStorage(stored);
            if (fromStorage) {
                loadedFromStorageRef.current = true;
                return fromStorage;
            }
            return { ...DEFAULT_COLUMN_VISIBILITY };
        },
    );

    const handleColumnVisibilityChange = (
        updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => {
        setColumnVisibility((prev) => {
            const next =
                typeof updater === "function" ? updater(prev) : updater;
            localStorage.setItem(
                COLUMN_VISIBILITY_STORAGE_KEY,
                JSON.stringify(next),
            );
            navigate({
                search: (prevSearch) => ({
                    ...prevSearch,
                    columns: visibilityToColumnParam(next),
                }),
                replace: true,
            });
            return next;
        });
    };

    useEffect(() => {
        if (loadedFromStorageRef.current && !columnsParam?.trim()) {
            loadedFromStorageRef.current = false;
            navigate({
                search: (prev) => ({
                    ...prev,
                    columns: visibilityToColumnParam(columnVisibility),
                }),
                replace: true,
            });
        }
    }, [columnVisibility, columnsParam, navigate]);

    const selectedColumns = useMemo(() => {
        return Object.keys(columnVisibility).filter(
            (key) => columnVisibility[key as keyof VisibilityState],
        );
    }, [columnVisibility]);

    const createManagementZonesRequest = useMutateRequest({
        url: `management_zones/auto_create/`,
        method: "post",
    });

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {compactFilters ? (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <Button
                        size="sm"
                        color={
                            activeMobileFilterCount > 0 ? "primary" : "default"
                        }
                        onPress={() => setMobileFiltersOpen(true)}
                        startContent={<FontAwesomeIcon icon={faFilter} />}
                    >
                        Filters
                        {activeMobileFilterCount > 0
                            ? ` (${activeMobileFilterCount})`
                            : ""}
                    </Button>
                    <Input
                        size="sm"
                        placeholder="Search..."
                        value={debouncedSearch}
                        onValueChange={setDebouncedSearch}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        className="flex-1"
                    />
                    <Button
                        color="primary"
                        size="sm"
                        as={Link}
                        to="/dashboard/fields/add?cameFrom=home/field-config"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add Fields
                    </Button>
                </div>
            ) : (
                <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                    <div className="flex flex-col lg:flex-row gap-2 items-center">
                        <FieldFilterBar
                            searchValue={search}
                            onSearchChange={(value) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        search:
                                            value === "" ? undefined : value,
                                    }),
                                });
                            }}
                            serviceProviderId={service_provider}
                            onServiceProviderChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        service_provider: id ?? "",
                                    }),
                                });
                            }}
                            serviceProviderOptions={serviceProviderOptions}
                            growerId={grower}
                            onGrowerChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        grower: id ?? "",
                                    }),
                                });
                            }}
                            growerOptions={growerOptions}
                            accountManagerId={account_manager}
                            onAccountManagerChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        account_manager: id ?? "",
                                    }),
                                });
                            }}
                            accountManagerOptions={accountManagerOptions}
                            enrollmentTier={enrollment_tier}
                            onEnrollmentTierChange={(id) => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        enrollment_tier: id ?? undefined,
                                    }),
                                });
                            }}
                        />
                        <Button
                            size="sm"
                            color={created_date ? "primary" : "default"}
                            onPress={() => {
                                navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        created_date: created_date
                                            ? undefined
                                            : dayjs().format("YYYY-MM-DD"),
                                    }),
                                });
                            }}
                            startContent={
                                created_date ? (
                                    <FontAwesomeIcon icon={faCheck} />
                                ) : null
                            }
                        >
                            Recently Created
                        </Button>
                        <Dropdown>
                            <DropdownTrigger>
                                <Button size="sm">
                                    Columns{" "}
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                disallowEmptySelection
                                closeOnSelect={false}
                                selectionMode="multiple"
                                selectedKeys={selectedColumns}
                            >
                                {(
                                    [
                                        { key: "grower", label: "Farm/CSP" },
                                        { key: "name", label: "Name" },
                                        {
                                            key: "activeCrop",
                                            label: "Active Crop",
                                        },
                                        {
                                            key: "plotStatus",
                                            label: "Plot Status",
                                        },
                                        {
                                            key: "irrigation",
                                            label: "Irrigation",
                                        },
                                        {
                                            key: "managementZone",
                                            label: "Management Zone",
                                        },
                                        {
                                            key: "connections",
                                            label: "Connections",
                                        },
                                        { key: "nPlan", label: "N Plan" },
                                        {
                                            key: "soil",
                                            label: "Soil Management",
                                        },
                                    ] as const
                                ).map(({ key, label }) => (
                                    <DropdownItem
                                        key={key}
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    [key]: !prev[key],
                                                }),
                                            )
                                        }
                                    >
                                        {label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                        <Button
                            color="primary"
                            size="sm"
                            as={Link}
                            to="/dashboard/fields/add?cameFrom=home/field-config"
                            startContent={<FontAwesomeIcon icon={faPlus} />}
                        >
                            Add Fields
                        </Button>
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    color="primary"
                                    size="sm"
                                    isDisabled={selectedFields.length === 0}
                                    startContent={
                                        <FontAwesomeIcon icon={faBars} />
                                    }
                                >
                                    Actions
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                                <DropdownItem
                                    key="set-default-product"
                                    onPress={() =>
                                        setShowDefaultProductForm(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faBucket}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Set Default Product
                                </DropdownItem>
                                <DropdownItem
                                    key="set-crop-planting-date"
                                    onPress={() => setShowPlantingForm(true)}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faLeaf}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Set Crop/Planting Date
                                </DropdownItem>
                                <DropdownItem
                                    key="create-zones"
                                    onPress={() => {
                                        createManagementZonesRequest.mutate({
                                            data: selectedFields.map(
                                                (field) => field.id,
                                            ),
                                        });
                                    }}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faLayerGroup}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Create Management Zones
                                </DropdownItem>
                                <DropdownItem
                                    key="create-plot-rx"
                                    onPress={() => setShowPlotRxForms(true)}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faDrawPolygon}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Create Plot Rx
                                </DropdownItem>
                                <DropdownItem
                                    key="export-plot-rx"
                                    onPress={() => setShowBulkExport(true)}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faFileZipper}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Export Plot Rx
                                </DropdownItem>
                                <DropdownItem
                                    key="log-plots-application"
                                    onPress={() =>
                                        setShowLogApplicationForm(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faClipboard}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Log Application
                                </DropdownItem>
                                <DropdownItem
                                    key="confirm-plots-location"
                                    onPress={() =>
                                        setShowPlotConfirmation(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faCheckDouble}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Confirm Plots Location
                                </DropdownItem>
                                <DropdownItem
                                    key="set-n-plan"
                                    onPress={() => setShowNPlanner(true)}
                                    startContent={
                                        <ScenarioPlannerIcon color="primary" />
                                    }
                                >
                                    Set N Plan
                                </DropdownItem>
                                <DropdownItem
                                    key="bulk-soil-management"
                                    onPress={() =>
                                        setSoilManagementFields(selectedFields)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faTractor}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Bulk Soil Management
                                </DropdownItem>
                                <DropdownItem
                                    key="download-shapefiles"
                                    onPress={downloadBoundaries}
                                    isDisabled={isDownloading}
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faFileZipper}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    {isDownloading
                                        ? "Downloading Shapefiles"
                                        : "Download Shapefiles"}
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
            )}
            <Divider />
            {isMobile ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {mobileIsLoading ? (
                        <div className="p-4 text-sm text-gray-500">
                            Loading fields...
                        </div>
                    ) : tableData.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500">
                            No fields found.
                        </div>
                    ) : (
                        <>
                            {tableData.map((field) => (
                                <div key={field.id} className="w-full p-1 pb-0">
                                    <MobileFieldConfigCard
                                        field={field}
                                        growersById={growersById}
                                        serviceProvidersById={
                                            serviceProvidersById
                                        }
                                        accountManagersById={
                                            accountManagersById
                                        }
                                        isConsultantOrSentinel={
                                            isConsultantOrSentinel
                                        }
                                        userIsSentinel={userIsSentinel}
                                        onPress={() => setDetailField(field)}
                                    />
                                </div>
                            ))}

                            {fieldsQuery.hasNextPage && (
                                <div className="p-2">
                                    <Button
                                        fullWidth
                                        size="sm"
                                        isLoading={
                                            fieldsQuery.isFetchingNextPage
                                        }
                                        onPress={() =>
                                            void fieldsQuery.fetchNextPage()
                                        }
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <TableV2
                    meta={meta}
                    data={tableData}
                    columns={columns}
                    sorting={sorting}
                    setSorting={setSorting}
                    manualSorting={true}
                    isLoading={isLoading}
                    onSelectionChange={handleSelectionChange}
                    hasNextPage={fieldsQuery.hasNextPage}
                    fetchNextPage={fieldsQuery.fetchNextPage}
                    isFetchingNextPage={fieldsQuery.isFetchingNextPage}
                    pinnedColumns={["selection", "grower", "name"]}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={handleColumnVisibilityChange}
                    showEndMessage
                    paddingBottom="20rem"
                    showScrollbar={true}
                />
            )}
            {showPlantingForm && (
                <BulkPlantingOperationForm
                    fields={selectedFields}
                    onFinish={() => {
                        setShowPlantingForm(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showPlotRxForms && (
                <BulkPlotRxForm
                    fields={selectedFields}
                    onFinish={() => {
                        setShowPlotRxForms(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showBulkExport && (
                <BulkRxExport
                    prescriptionsToExport={selectedFields
                        .filter(
                            (field) =>
                                field.latest_plot_prescription_id !== null,
                        )
                        .map((field) => {
                            return {
                                fieldId: field.id,
                                prescriptionId:
                                    field.latest_plot_prescription_id as number,
                                fieldName: field.name,
                            };
                        })}
                    onFinish={() => {
                        setShowBulkExport(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showLogApplicationForm && (
                <BulkLogApplication
                    showPlotsEstablishment={true}
                    fields={selectedFields}
                    onFinish={() => {
                        setShowLogApplicationForm(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showPlotConfirmation && (
                <BulkConfirmPlots
                    selectedFields={selectedFields}
                    onFinish={() => {
                        setShowPlotConfirmation(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {plannerField !== null && (
                <FieldPlanner
                    field={plannerField}
                    plannerIsOpen={plannerField !== null}
                    onClose={() => {
                        setPlannerField(null);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showNPlanner && (
                <BulkNPlanner
                    fields={selectedFields}
                    onClose={() => {
                        setShowNPlanner(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            <Modal
                isOpen={soilManagementFields.length > 0}
                onClose={() => setSoilManagementFields([])}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        <div className="flex flex-row gap-2 items-center">
                            Soil & Management
                            <FontAwesomeIcon
                                icon={faTractor}
                                className="text-primary-500"
                            />
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <SoilManagement
                            fields={soilManagementFields.map((field) => ({
                                id: field.id,
                                soil_configuration: field.soil_configuration,
                                soil_organic_matter: field.soil_organic_matter,
                                has_drainage: field.has_drainage,
                                typical_N_applied_pounds_per_acre:
                                    field.typical_N_applied_pounds_per_acre,
                                typical_yield_bushels_per_acre:
                                    field.typical_yield_bushels_per_acre,
                                crop_cycle: field.current_crop_cycle,
                            }))}
                            required={
                                soilManagementFields.filter(
                                    (field) => field.soil_management_required,
                                ).length > 0
                            }
                            onFinish={() => {
                                setSoilManagementFields([]);
                                fieldsQuery.refetch();
                            }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Modal
                isOpen={showDefaultProductForm}
                onClose={() => setShowDefaultProductForm(false)}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>Set Default Product</ModalHeader>
                    <ModalBody>
                        <ProductAutoComplete
                            selectedProductId={selectedDefaultProductId}
                            setSelectedProductId={(productId) => {
                                setSelectedDefaultProductId(productId);
                            }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onPress={() => {
                                setShowDefaultProductForm(false);
                                patchField.mutate({
                                    data: selectedFields.map((field) => ({
                                        id: field.id,
                                        default_product:
                                            selectedDefaultProductId,
                                    })),
                                });
                            }}
                            isLoading={patchField.isPending}
                        >
                            Set Default Product
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <MobileFilterDrawer
                isOpen={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                searchValue={search}
                serviceProviderId={service_provider}
                growerId={grower}
                accountManagerId={account_manager}
                createdDate={created_date}
                showServiceProviderFilter={userIsSentinel}
                showGrowerFilter={isConsultantOrSentinel}
                showAccountManagerFilter={userIsSentinel}
                showRecentlyCreated={true}
                serviceProviderOptions={serviceProviderOptions}
                growerOptions={growerOptions}
                accountManagerOptions={accountManagerOptions}
                ordering={ordering}
                sortOptions={FIELD_CONFIG_SORT_OPTIONS}
                onApply={({
                    searchValue,
                    serviceProviderId,
                    growerId,
                    accountManagerId,
                    recentlyCreated,
                    sortKey,
                    sortDesc,
                }) => {
                    if (sortKey) {
                        setSorting([{ id: sortKey, desc: sortDesc }]);
                    }
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: searchValue,
                            service_provider: serviceProviderId ?? "",
                            grower: growerId ?? "",
                            account_manager: accountManagerId ?? "",
                            created_date: recentlyCreated
                                ? dayjs().format("YYYY-MM-DD")
                                : undefined,
                        }),
                    });
                }}
                onClear={() => {
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            search: "",
                            service_provider: "",
                            grower: "",
                            account_manager: "",
                            created_date: undefined,
                        }),
                    });
                }}
            />
            <MobileFieldConfigDetailDrawer
                field={detailField}
                isOpen={!!detailField}
                onClose={() => setDetailField(null)}
                growersById={growersById}
                serviceProvidersById={serviceProvidersById}
                accountManagersById={accountManagersById}
                isConsultantOrSentinel={isConsultantOrSentinel}
                userIsSentinel={userIsSentinel}
                onEditNPlan={(field) => {
                    setDetailField(null);
                    setPlannerField(field);
                }}
                onEditSoil={(field) => {
                    setDetailField(null);
                    setSoilManagementFields([field]);
                }}
            />
        </div>
    );
}
